import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CostingService } from '../../services/costing.service';
import { AuthService } from '../../services/auth.service';
import { OUTPUT_STAGES, OutputStage, ProductionOutput } from '../../interfaces/costing.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';
import OrderPicker from './OrderPicker';

const ProductionOutputPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.COSTING_MANAGE);
  const [rows, setRows] = useState<ProductionOutput[]>([]);
  const [filterOrder, setFilterOrder] = useState('');
  const [form, setForm] = useState({ buyerOrderId: '', outputDate: toLocalIsoDate(), stage: 'SEWN' as OutputStage, quantity: '', notes: '' });

  const load = useCallback(async () => {
    try { setRows(await CostingService.listOutputs(institutionId, { buyerOrderId: filterOrder || undefined })); }
    catch (err) { toast.showApiError(err, 'Could not load production output'); }
  }, [institutionId, filterOrder, toast]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await CostingService.createOutput(institutionId, {
        buyerOrderId: form.buyerOrderId, outputDate: form.outputDate, stage: form.stage, quantity: Number(form.quantity), notes: form.notes || null,
      });
      toast.showSuccess('Output recorded');
      setForm(s => ({ ...s, quantity: '', notes: '' }));
      await load();
    } catch (err) { toast.showApiError(err, 'Could not record the output'); }
  };

  const remove = async (id: string) => {
    try { await CostingService.deleteOutput(id); toast.showSuccess('Output removed'); await load(); }
    catch (err) { toast.showApiError(err, 'Could not remove the output'); }
  };

  return (
    <Box sx={{ p: 2 }} data-help-page="costing.output.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Production output</Typography>
        <Button data-help="costing.output.orders" component={RouterLink} to="/costing/orders">Order costs</Button>
      </Box>
      {canManage && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }} data-help-section="costing.output.form">
          <Box sx={{ flex: '1 1 240px', maxWidth: 320 }}>
            <OrderPicker data-help="costing.output.order" always label="Order" required value={form.buyerOrderId} onChange={id => setForm(s => ({ ...s, buyerOrderId: id }))} />
          </Box>
          <TextField data-help="costing.output.date" sx={{ flex: '1 1 150px', maxWidth: 180 }} size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
            value={form.outputDate} onChange={e => setForm(s => ({ ...s, outputDate: e.target.value }))} />
          <TextField data-help="costing.output.stage" sx={{ flex: '1 1 130px', maxWidth: 160 }} size="small" select label="Stage" value={form.stage}
            onChange={e => setForm(s => ({ ...s, stage: e.target.value as OutputStage }))}>
            {OUTPUT_STAGES.map(st => <MenuItem key={st} value={st}>{st}</MenuItem>)}
          </TextField>
          <TextField data-help="costing.output.quantity" sx={{ flex: '1 1 110px', maxWidth: 140 }} size="small" type="number" label="Quantity" value={form.quantity}
            onChange={e => setForm(s => ({ ...s, quantity: e.target.value }))} />
          <TextField data-help="costing.output.notes" sx={{ flex: '1 1 200px', maxWidth: 300 }} size="small" label="Notes" value={form.notes}
            onChange={e => setForm(s => ({ ...s, notes: e.target.value }))} inputProps={{ maxLength: 500 }} />
          <Button data-help="costing.output.record" variant="contained" disabled={!form.buyerOrderId || !form.quantity || Number(form.quantity) <= 0} onClick={add}>Record</Button>
        </Paper>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }} data-help-section="costing.output.filters">
        <Box sx={{ flex: '1 1 240px', maxWidth: 320 }}>
          <OrderPicker data-help="costing.output.filterOrder" always label="Filter by order" openOnly={false} value={filterOrder} onChange={setFilterOrder} />
        </Box>
      </Box>
      <TableContainer component={Paper} data-help-section="costing.output.table">
        <Table size="small" data-testid="output-table">
          <TableHead><TableRow><TableCell data-help="costing.output.col.date">Date</TableCell><TableCell data-help="costing.output.col.order">Order</TableCell><TableCell data-help="costing.output.col.stage">Stage</TableCell>
            <TableCell align="right" data-help="costing.output.col.quantity">Quantity</TableCell><TableCell data-help="costing.output.col.notes">Notes</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {rows.map(o => (
              <TableRow key={o.id}><TableCell>{o.outputDate}</TableCell><TableCell>{o.orderNumber}</TableCell><TableCell>{o.stage}</TableCell>
                <TableCell align="right">{o.quantity}</TableCell><TableCell>{o.notes}</TableCell>
                <TableCell align="right">{canManage && <Button data-help="costing.output.remove" size="small" color="error" onClick={() => remove(o.id)}>Remove</Button>}</TableCell></TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6}>No output recorded.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProductionOutputPage;
